import React from 'react';
import { View, StyleSheet, ViewStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { formatCurrency } from '@/utils/common/formatting';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import type { ThemeContextValue } from '@/hooks/residents/useThemeColors';
import {
  PAYMENT_TYPOGRAPHY,
  PAYMENT_SPACING,
  PAYMENT_BORDER_RADIUS,
} from '@/constants/residents/mobile/paymentDesignTokens';

type Variant = 'inline' | 'card';

interface AccountCreditIndicatorProps {
  amount: number;
  variant?: Variant;
  style?: ViewStyle;
}

export const AccountCreditIndicator: React.FC<AccountCreditIndicatorProps> = ({
  amount,
  variant = 'inline',
  style,
}) => {
  const theme = useThemeColors();
  if (!(amount > 0)) return null;

  const s = variant === 'card' ? buildCardStyles(theme) : buildInlineStyles(theme);

  return (
    <View style={[s.container, style]}>
      <Text style={s.label}>Account credit</Text>
      <Text style={s.amount}>{formatCurrency(amount)}</Text>
    </View>
  );
};

const buildInlineStyles = (theme: ThemeContextValue) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: PAYMENT_SPACING.xs,
    paddingHorizontal: PAYMENT_SPACING.sm,
  },
  label: {
    ...PAYMENT_TYPOGRAPHY.textXsMedium,
    color: theme.colors.text.muted,
  },
  amount: {
    ...PAYMENT_TYPOGRAPHY.textXsMedium,
    color: theme.semantic.status.success,
  },
});

const buildCardStyles = (theme: ThemeContextValue) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.glass.buttonBg,
    borderRadius: PAYMENT_BORDER_RADIUS.md,
    padding: PAYMENT_SPACING.md,
  },
  label: {
    ...PAYMENT_TYPOGRAPHY.bodySmMedium,
    color: theme.colors.text.muted,
  },
  amount: {
    ...PAYMENT_TYPOGRAPHY.bodySmMedium,
    color: theme.semantic.status.success,
  },
});
